import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';

import { RestManager } from '@/newApi/services/rest/RestManager';
import type { QueryOptions, UseMutationOptions } from '@tanstack/vue-query';
import { queryOptions, useQueryClient } from '@tanstack/vue-query';
import { ResgateManager, ResErr } from '@/newApi/services/ResgateManager';
import type { PayRangeTreeNode, PayRangeAddOption, BulkAddInput, BulkOperationsPayRangesResponse, BulkPatchPayRangeAttributesInput, BulkDeletePayRangesInput, PayRangeActualSalary, BulkPayRangeCountrySalaryType, JobPayRangesResponse } from '@sysarb/sysarb-types-v2/services/v1/job/payRange';
import type { CountryCode, EmploymentSalaryType } from '@sysarb/sysarb-types/general/enums';
export type { PayRangeAddOption, BulkPatchPayRangeAttributesInput as BulkPatchPayload, BulkPayRangeCountrySalaryType } from '@sysarb/sysarb-types-v2/services/v1/job/payRange';

const jobService = new RestManager(`https://${getApiHost()}/job-service`);

// ─── Request / Response Types ─────────────────────────────────────────────────

export type PayRangesChangesResponse = { changeID: string };
export type BulkPatchResponse = { success: boolean };
export type PayRangeNoOptionsResponse = { nodeIds: string[] };

// ─── API Definition ───────────────────────────────────────────────────────────

type ExternalJobPayRangesOptions = {
    queries: {
        getPayRangeChangeId: () => QueryOptions<PayRangesChangesResponse, Error, PayRangesChangesResponse>;
        getPayRangeTree: () => QueryOptions<PayRangeTreeNode[], Error, PayRangeTreeNode[]>;
        getPayRangeActualSalaries: () => QueryOptions<PayRangeActualSalary[], Error, PayRangeActualSalary[]>;
        getDraftNodeOptions: (nodeId: string) => QueryOptions<PayRangeAddOption[], Error, PayRangeAddOption[]>;
        getPayRangeNoOptions: () => QueryOptions<PayRangeNoOptionsResponse, Error, PayRangeNoOptionsResponse>;
        getJobPayRanges: (jobId: string | null, country: CountryCode, salaryType?: EmploymentSalaryType) => QueryOptions<JobPayRangesResponse, Error, JobPayRangesResponse>;
    };
    mutations: {
        bulkAdd: () => UseMutationOptions<BulkOperationsPayRangesResponse | null, Error, BulkAddInput>;
        bulkDelete: () => UseMutationOptions<BulkOperationsPayRangesResponse | null, Error, BulkDeletePayRangesInput>;
        initBaseData: () => UseMutationOptions<void, Error, void>;
        bulkPatch: () => UseMutationOptions<BulkPatchResponse, Error, BulkPatchPayRangeAttributesInput>;
        bulkUpdateSalaryTypes: () => UseMutationOptions<BulkPatchResponse, Error, BulkPayRangeCountrySalaryType>;
    };
};

export const externalPayRanges: ExternalJobPayRangesOptions = {
    queries: {
        getPayRangeChangeId: (): QueryOptions<PayRangesChangesResponse, Error, PayRangesChangesResponse> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.jobs.pay-ranges-external.changes`;
            return queryOptions<PayRangesChangesResponse, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['external-pay-ranges-change-id', topic],
                queryFn: async (): Promise<PayRangesChangesResponse> => {
                    return ResgateManager.model<PayRangesChangesResponse>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
            });
        },
        getPayRangeTree: () => {
            const { instance } = useLoginStore();
            const path = `v1/${instance}/pay-ranges-external/tree`;
            return queryOptions<PayRangeTreeNode[], Error, PayRangeTreeNode[]>({
                queryKey: ['external-pay-ranges-tree', instance, path],
                queryFn: async (): Promise<PayRangeTreeNode[]> => {
                    return jobService.get<PayRangeTreeNode[]>(path);
                },
                select: (data) => data,
            });
        },
        getPayRangeActualSalaries: (): QueryOptions<PayRangeActualSalary[], Error, PayRangeActualSalary[]> => {
            const { instance } = useLoginStore();
            const path = `v1/${instance}/pay-ranges-external/tree/actual-salaries`;
            return queryOptions<PayRangeActualSalary[], Error, PayRangeActualSalary[]>({
                queryKey: ['external-pay-ranges-actual-salaries', instance, path],
                queryFn: async (): Promise<PayRangeActualSalary[]> => jobService.get<PayRangeActualSalary[]>(path),
            });
        },
        getDraftNodeOptions: (nodeId: string): QueryOptions<PayRangeAddOption[], Error, PayRangeAddOption[]> => {
            const { instance } = useLoginStore();
            return queryOptions<PayRangeAddOption[], Error, PayRangeAddOption[]>({
                queryKey: ['external-pay-ranges-draft-node-options', instance, nodeId],
                queryFn: async (): Promise<PayRangeAddOption[]> => {
                    return jobService.get<PayRangeAddOption[]>(`v1/${instance}/pay-ranges-external/${nodeId}/options`);
                },
                select: (data: PayRangeAddOption[]) => data,
            });
        },

        /**
         * The nodes whose add menu would come back empty, so their "+" can render disabled without
         * opening the menu first.
         */
        getPayRangeNoOptions: (): QueryOptions<PayRangeNoOptionsResponse, Error, PayRangeNoOptionsResponse> => {
            const { instance } = useLoginStore();
            const path = `v1/${instance}/pay-ranges-external/no-options`;
            return queryOptions<PayRangeNoOptionsResponse, Error, PayRangeNoOptionsResponse>({
                queryKey: ['external-pay-ranges-no-options', instance, path],
                queryFn: async (): Promise<PayRangeNoOptionsResponse> => {
                    return jobService.get<PayRangeNoOptionsResponse>(path);
                },
            });
        },

        /**
         * The external pay range nodes the job profile's active employees are assigned to in a given
         * country, together with an aggregated range (lowest min, highest max) across those nodes.
         *
         * The job id is the base64 encoded external job profile id, the same id used by
         * `v3/:instance/external/job/:id`. External pay ranges have no draft state.
         */
        getJobPayRanges: (jobId: string | null, country: CountryCode, salaryType?: EmploymentSalaryType): QueryOptions<JobPayRangesResponse, Error, JobPayRangesResponse> => {
            const { instance } = useLoginStore();
            const urlEncodedId = jobId != null ? encodeURIComponent(jobId) : '';
            // When salaryType is given, min and max are converted to that salary type by the backend
            const query = salaryType != null ? `?salaryType=${salaryType}` : '';
            const path = `v1/${instance}/job/${urlEncodedId}/pay-ranges-external/${country}${query}`;
            return queryOptions<JobPayRangesResponse, Error, JobPayRangesResponse>({
                queryKey: ['external-job-pay-ranges', instance, jobId, country, path],
                queryFn: async (): Promise<JobPayRangesResponse> => {
                    return jobService.post<JobPayRangesResponse>({}, path);
                },
                enabled: () => jobId != null,
            });
        },
    },

    mutations: {
        bulkAdd: (): UseMutationOptions<BulkOperationsPayRangesResponse | null, Error, BulkAddInput> => {
            const queryClient = useQueryClient();
            return {
                mutationFn: async (payload: BulkAddInput): Promise<BulkOperationsPayRangesResponse | null> => {
                    const { instance } = useLoginStore();
                    return jobService.post<BulkOperationsPayRangesResponse>(payload, `v1/${instance}/pay-ranges-external/tree`);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-tree'] });
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-actual-salaries'] });
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-draft-node-options'] });
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-no-options'] });
                },
            };
        },

        bulkPatch: (): UseMutationOptions<BulkPatchResponse, Error, BulkPatchPayRangeAttributesInput> => {
            const queryClient = useQueryClient();
            return {
                mutationFn: async (payload: BulkPatchPayRangeAttributesInput): Promise<BulkPatchResponse> => {
                    const { instance } = useLoginStore();
                    return jobService.post<BulkPatchResponse>(payload, `v1/${instance}/pay-ranges-external`);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-tree'] });
                },
            };
        },

        bulkUpdateSalaryTypes: (): UseMutationOptions<BulkPatchResponse, Error, BulkPayRangeCountrySalaryType> => {
            const queryClient = useQueryClient();
            return {
                mutationFn: async (payload: BulkPayRangeCountrySalaryType): Promise<BulkPatchResponse> => {
                    const { instance } = useLoginStore();
                    return jobService.post<BulkPatchResponse>(payload, `v1/${instance}/pay-ranges-external/salary-type`);
                },
                onSuccess: (_data, payload): void => {
                    const salaryTypeByCountry = new Map(payload.map((row) => [row.country, row.salaryType]));
                    const { queryKey } = externalPayRanges.queries.getPayRangeTree();
                    queryClient.setQueryData<PayRangeTreeNode[]>(
                        queryKey,
                        (nodes) => nodes?.map((node) => {
                            const salaryType = salaryTypeByCountry.get(node.country);
                            return salaryType != null && salaryType !== node.salaryType ? { ...node, salaryType } : node;
                        }),
                    );
                    // The pay range values the user typed in are left as they are, but the actual salaries
                    // are aggregated in the country's salary type, so they have to be recalculated server-side.
                    void queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-actual-salaries'] });
                },
                onError: (): void => {
                    const { queryKey } = externalPayRanges.queries.getPayRangeTree();
                    void queryClient.invalidateQueries({ queryKey });
                },
            };
        },

        bulkDelete: (): UseMutationOptions<BulkOperationsPayRangesResponse | null, Error, BulkDeletePayRangesInput> => {
            const queryClient = useQueryClient();
            return {
                mutationFn: async (payload: BulkDeletePayRangesInput): Promise<BulkOperationsPayRangesResponse | null> => {
                    const { instance } = useLoginStore();
                    return jobService.delete<BulkOperationsPayRangesResponse>(`v1/${instance}/pay-ranges-external/tree`, undefined, { data: payload });
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-tree'] });
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-draft-node-options'] });
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-no-options'] });
                },
            };
        },
        initBaseData: (): UseMutationOptions<void, Error, void> => {
            const queryClient = useQueryClient();
            return {
                mutationFn: async (): Promise<void> => {
                    const { instance } = useLoginStore();
                    await jobService.post<void>( {}, `v1/${instance}/pay-ranges-external/init`);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-tree'] });
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-actual-salaries'] });
                    await queryClient.invalidateQueries({ queryKey: ['external-pay-ranges-no-options'] });
                },
            };
        },
    },
};
